import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as LogoMark } from '../assets/logo.svg';

const NAV_EXPANDED_INNER = 112;
/** Compact toolbar (~56–58px class); fits scrolled logo without wasted vertical space */
const NAV_COLLAPSED = 58;
const NAV_TOP_INSET = 10;
/** Padding under expanded mobile browser chrome (iOS Safari / Chrome) */
const MOBILE_BROWSER_TOP_GAP = 52;
/** visualViewport.offsetTop — open gap only past this (hysteresis avoids flicker) */
const VP_OPEN_PX = 14;
/** Close gap only below this */
const VP_CLOSE_PX = 6;
/** innerHeight − vv.height — URL chrome visible when large */
const CHROME_OPEN_PX = 34;
const CHROME_CLOSE_PX = 16;
/** Below this scroll position: always hide gap (fresh load, scrolled back to top) */
const SCROLL_TOP_GAP_OFF_PX = 72;
/** Burger bars morph duration — keep unmount delay slightly after this (mobile menu close) */
const MENU_BURGER_MORPH_MS = 200;
const MENU_CLOSE_UNMOUNT_MS = MENU_BURGER_MORPH_MS + 24;
/** Desktop: interpolate nav + logo over this scroll distance (starts at scroll 0 — avoids content hiding under the oversized logo before transition) */
const DESKTOP_SCROLL_RANGE_PX = 84;
/** Mobile keeps a discrete flip so tap targets stay predictable */
const MOBILE_SCROLL_THRESHOLD_PX = 70;
/** Expanded logo caps (desktop): scales down on narrow/short viewports */
const LOGO_EXPANDED_DESKTOP_MAX = 164.16;
const LOGO_EXPANDED_MOBILE = 139.68;
const LOGO_COLLAPSED_DESKTOP = 58;
const LOGO_COLLAPSED_MOBILE = 50;
/** 2rem at default 16px root — matches spacer / logo dock */
const LOGO_LEFT_COLLAPSED_PX = 32;
/** Frosted toolbar stays off until this fraction of the logo shrink; then ramps to full glass at t→1 */
const GLASS_BAR_SCROLL_START_T = 0.92;

const clamp01 = (x) => Math.min(1, Math.max(0, x));
const lerp = (a, b, t) => a + (b - a) * t;

/** Layered elevation under the glass, scaled by glass opacity */
function scrolledNavShadow(opacity) {
  const o = clamp01(opacity);
  if (o < 0.02) return 'none';
  return `0 1px 3px rgba(0, 0, 0, ${0.12 * o}), 0 4px 14px rgba(0, 0, 0, ${0.14 * o}), 0 14px 36px rgba(0, 0, 0, ${0.12 * o})`;
}

export default function Navbar({ cartCount, onOpenCart }) {
  const [navMetrics, setNavMetrics] = useState(() => ({
    scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
    isMobile:
      typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 900px)').matches
        : false,
    vw: typeof window !== 'undefined' ? window.innerWidth : 1200,
    vh: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  const [menuOpen, setMenuOpen] = useState(false);
  /** Drive overlay opacity fade-in after open */
  const [menuOverlayFadeIn, setMenuOverlayFadeIn] = useState(false);
  /** Drive overlay opacity fade-out before unmount */
  const [menuOverlayClosing, setMenuOverlayClosing] = useState(false);
  /** Overlay close control: false = bars, true = X (animates open/close) */
  const [overlayMenuIconExpanded, setOverlayMenuIconExpanded] = useState(false);
  /** Viewport box for portaled menu close control (matches toolbar burger) */
  const [burgerAnchorRect, setBurgerAnchorRect] = useState(null);
  /** Shell padding-top on narrow viewports — driven by hysteresis, not raw offsetTop */
  const [mobileTopGap, setMobileTopGap] = useState(0);
  const gapActiveRef = useRef(false);
  const burgerRef = useRef(null);

  const beginMenuClose = useCallback((afterTransition) => {
    setOverlayMenuIconExpanded(false);
    setMenuOverlayClosing(true);
    window.setTimeout(() => {
      setMenuOpen(false);
      setBurgerAnchorRect(null);
      setMenuOverlayClosing(false);
      setMenuOverlayFadeIn(false);
      afterTransition?.();
    }, MENU_CLOSE_UNMOUNT_MS);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const vv = window.visualViewport;

    const syncMobileChromePad = () => {
      const y = window.scrollY;
      setNavMetrics({
        scrollY: y,
        isMobile: mq.matches,
        vw: window.innerWidth,
        vh: window.innerHeight,
      });

      if (!mq.matches) {
        gapActiveRef.current = false;
        setMobileTopGap(0);
        return;
      }

      if (y <= SCROLL_TOP_GAP_OFF_PX) {
        gapActiveRef.current = false;
        setMobileTopGap(0);
        return;
      }

      let ot = 0;
      let chromeGap = 0;
      if (vv) {
        ot = vv.offsetTop;
        chromeGap = Math.max(0, window.innerHeight - vv.height);
      }

      const active = gapActiveRef.current;

      let next = active;
      if (!active) {
        if (ot >= VP_OPEN_PX || chromeGap >= CHROME_OPEN_PX) {
          next = true;
        }
      } else if (ot <= VP_CLOSE_PX && chromeGap <= CHROME_CLOSE_PX) {
        next = false;
      }

      if (next !== active) {
        gapActiveRef.current = next;
        setMobileTopGap(next ? MOBILE_BROWSER_TOP_GAP : 0);
      }
    };

    const rafRef = { id: 0 };
    const schedule = () => {
      if (rafRef.id) return;
      rafRef.id = requestAnimationFrame(() => {
        rafRef.id = 0;
        syncMobileChromePad();
      });
    };

    syncMobileChromePad();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    mq.addEventListener('change', syncMobileChromePad);

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      mq.removeEventListener('change', syncMobileChromePad);
      if (rafRef.id) cancelAnimationFrame(rafRef.id);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') beginMenuClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, beginMenuClose]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    setOverlayMenuIconExpanded(false);
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setOverlayMenuIconExpanded(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    setMenuOverlayFadeIn(false);
    setMenuOverlayClosing(false);
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setMenuOverlayFadeIn(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [menuOpen]);

  /** Keep portaled close button aligned with toolbar burger (layout + mobile chrome) */
  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    const sync = () => {
      const el = burgerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setBurgerAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    sync();
    window.addEventListener('resize', sync);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    return () => {
      window.removeEventListener('resize', sync);
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
    };
  }, [menuOpen, navMetrics.scrollY, mobileTopGap]);

  const { scrollY, isMobile, vw, vh } = navMetrics;
  /** 0–1: desktop maps scroll continuously; mobile stays a step at the threshold */
  const t = isMobile
    ? scrollY > MOBILE_SCROLL_THRESHOLD_PX
      ? 1
      : 0
    : clamp01(scrollY / DESKTOP_SCROLL_RANGE_PX);
  /** 0–1: frosted bar + shadow; smoothstep softens the short final segment of scroll */
  const glassLinear = clamp01((t - GLASS_BAR_SCROLL_START_T) / (1 - GLASS_BAR_SCROLL_START_T));
  const glassReveal =
    glassLinear * glassLinear * (3 - 2 * glassLinear);
  /** Hero-edge tint fades out as glass appears */
  const heroTintOpacity = 1 - glassReveal;

  const collapsedLogo = isMobile ? LOGO_COLLAPSED_MOBILE : LOGO_COLLAPSED_DESKTOP;
  const expandedLogo = isMobile
    ? LOGO_EXPANDED_MOBILE
    : Math.max(
        collapsedLogo,
        Math.min(LOGO_EXPANDED_DESKTOP_MAX, vw * 0.24, vh * 0.23),
      );
  const logoSize = lerp(expandedLogo, collapsedLogo, t);

  const logoTop = lerp(25, isMobile ? 4 : 0, t);
  const logoLeft = lerp(25, LOGO_LEFT_COLLAPSED_PX, t);

  const spacerWidth = lerp(25 + expandedLogo, LOGO_LEFT_COLLAPSED_PX + collapsedLogo, t);

  const paddingTop = lerp(NAV_TOP_INSET, 0, t);
  const navHeightPx = lerp(NAV_TOP_INSET + NAV_EXPANDED_INNER, NAV_COLLAPSED, t);

  const navEaseTransition =
    'padding-top 0.45s cubic-bezier(0.4, 0, 0.2, 1), height 0.45s cubic-bezier(0.4, 0, 0.2, 1)';

  const openMobileMenu = () => {
    const el = burgerRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setBurgerAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    setMenuOpen(true);
  };

  const navShellStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    paddingTop: mobileTopGap,
    boxSizing: 'border-box',
    transition: 'padding-top 0.22s ease-out',
    pointerEvents: 'none',
  };

  const navStyle = {
    position: 'relative',
    width: '100%',
    height: navHeightPx,
    paddingTop,
    paddingLeft: 0,
    paddingRight: '2rem',
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    transition: isMobile ? navEaseTransition : 'none',
    background: 'transparent',
    boxShadow: 'none',
    isolation: 'isolate',
  };

  const linkStyle = {
    color: 'rgba(255,255,255,0.85)',
    textDecoration: 'none',
    fontSize: '.9rem',
    fontWeight: 500,
    letterSpacing: '.02em',
    transition: 'color .18s',
  };

  const scrollTo = (id) => {
    if (menuOpen) {
      beginMenuClose(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const closeMobileMenuWithMorph = () => beginMenuClose();

  const logoSvgStyle = {
    flexShrink: 0,
    display: 'block',
    width: logoSize,
    height: logoSize,
    transition: isMobile
      ? 'width 0.45s cubic-bezier(0.4, 0, 0.2, 1), height 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
      : 'none',
  };

  const glassLayerTransition = isMobile
    ? 'opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.35s ease'
    : 'none';
  const heroTintTransition = isMobile ? 'opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';

  /** Horizontal band: clearer teal under logo (left); fades so vertical frost reads as before on the right */
  const frostedGlassVertical =
    'linear-gradient(180deg, rgba(9, 64, 77, 0.55) 0%, rgba(9, 52, 62, 0.42) 55%, rgba(8, 44, 52, 0.38) 100%)';
  /** Logo-edge tint; mobile fades across 0–50% width; desktop keeps the tighter ramp to 34% */
  const frostedGlassLeadingTintDesktop = [
    [
      'linear-gradient(',
      'to right,',
      'rgba(2, 16, 22, 0.62) 0%,',
      'rgba(3, 26, 34, 0.44) 6%,',
      'rgba(5, 38, 46, 0.26) 13%,',
      'rgba(7, 48, 58, 0.12) 20%,',
      'rgba(9, 54, 64, 0.04) 27%,',
      'transparent 34%',
      ')',
    ].join(''),
    [
      'radial-gradient(',
      'ellipse 13% 185% at 10% 50%,',
      'rgba(5, 42, 52, 0.38) 0%,',
      'rgba(7, 50, 60, 0.17) 42%,',
      'rgba(9, 56, 68, 0.06) 62%,',
      'rgba(11, 60, 70, 0.02) 78%,',
      'transparent 92%',
      ')',
    ].join(''),
  ].join(', ');
  const frostedGlassLeadingTintMobile = [
    [
      'linear-gradient(',
      'to right,',
      'rgba(1, 11, 15, 0.82) 0%,',
      'rgba(2, 22, 28, 0.58) 9%,',
      'rgba(4, 34, 42, 0.38) 19%,',
      'rgba(6, 44, 54, 0.2) 29%,',
      'rgba(8, 52, 62, 0.09) 40%,',
      'transparent 50%',
      ')',
    ].join(''),
    [
      'radial-gradient(',
      'ellipse 28% 185% at 22% 50%,',
      'rgba(4, 34, 42, 0.52) 0%,',
      'rgba(7, 50, 60, 0.24) 42%,',
      'rgba(9, 56, 68, 0.09) 65%,',
      'rgba(11, 62, 72, 0.03) 82%,',
      'transparent 94%',
      ')',
    ].join(''),
  ].join(', ');
  const frostedGlassLeadingTint = isMobile
    ? frostedGlassLeadingTintMobile
    : frostedGlassLeadingTintDesktop;

  const navGlassBackground = `${frostedGlassLeadingTint}, ${frostedGlassVertical}`;

  const navGlassStyle = {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    opacity: glassReveal,
    transition: glassLayerTransition,
    WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
    backdropFilter: 'blur(20px) saturate(1.4)',
    background: navGlassBackground,
    boxShadow: scrolledNavShadow(glassReveal),
    borderBottom: `1px solid rgba(255, 255, 255, ${0.14 * glassReveal})`,
  };

  const navHeroTintStyle = {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    opacity: heroTintOpacity,
    transition: heroTintTransition,
    background:
      'linear-gradient(180deg, rgba(9,64,77,0.94) 0%, rgba(9,64,77,0.55) 55%, rgba(9,64,77,0) 100%)',
  };

  return (
    <div className="nav-shell" style={navShellStyle}>
    <nav
      style={{ ...navStyle, overflow: 'visible' }}
      role="banner"
      className={glassReveal >= 0.98 ? 'main-nav main-nav--scrolled' : 'main-nav main-nav--top'}
    >
      <div className="nav-backdrop-layer" aria-hidden style={navHeroTintStyle} />
      <div className="nav-backdrop-layer" aria-hidden style={navGlassStyle} />

      {/* Reserves horizontal space so links stay right-aligned while logo is absolutely positioned at top */}
      <div
        className="nav-logo-spacer"
        aria-hidden="true"
        style={{
          width: spacerWidth,
          transition: isMobile ? 'width 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        }}
      />

      <a
        href="#home"
        className="nav-logo-home"
        onClick={(e) => { e.preventDefault(); scrollTo('home'); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
          top: logoTop,
          left: logoLeft,
          transition: isMobile
            ? 'top 0.45s cubic-bezier(0.4, 0, 0.2, 1), left 0.45s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none',
        }}
        aria-label="Thai Express Seychelles home"
      >
        <span className="nav-logo-gold-wrap" style={{ display: 'flex', alignItems: 'center' }}>
          <svg width={0} height={0} aria-hidden style={{ position: 'absolute', overflow: 'hidden' }}>
            <defs>
              {/* Matches CSS linear-gradient(to right, #BF953F, #FCF6BA, …) — stops evenly spaced */}
              <linearGradient id="navLogoGoldGrad" x1="0" y1="0.5" x2="1" y2="0.5">
                <stop offset="0%" stopColor="#BF953F" />
                <stop offset="25%" stopColor="#FCF6BA" />
                <stop offset="50%" stopColor="#B38728" />
                <stop offset="75%" stopColor="#FBF5B7" />
                <stop offset="100%" stopColor="#AA771C" />
              </linearGradient>
            </defs>
          </svg>
          <LogoMark className="nav-logo-mark nav-logo-mark--gold" style={logoSvgStyle} aria-hidden="true" />
        </span>
      </a>

      <div
        style={{ display: 'flex', alignItems: 'center', gap: '2rem', paddingLeft: '2rem' }}
        className="nav-desktop"
      >
        {[
          { label: 'Menu', id: 'menu' },
          { label: "Today's Specials", id: 'specials' },
          { label: 'Our Story', id: 'story' },
          { label: 'Reviews', id: 'feedback' },
          { label: 'Contact', id: 'footer' },
        ].map((link) => (
          <a
            key={link.id}
            href={`#${link.id}`}
            onClick={(e) => { e.preventDefault(); scrollTo(link.id); }}
            style={linkStyle}
            onMouseEnter={(e) => (e.target.style.color = 'var(--gold-light)')}
            onMouseLeave={(e) => (e.target.style.color = 'rgba(255,255,255,0.85)')}
          >
            {link.label}
          </a>
        ))}

        <button
          type="button"
          onClick={onOpenCart}
          style={{
            background: 'var(--red)',
            color: 'white',
            border: 'none',
            padding: '.55rem 1.4rem',
            borderRadius: 'var(--radius-pill)',
            fontWeight: 700,
            fontSize: '.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '.5rem',
            transition: 'all .2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--red-dark)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--red)')}
          aria-label={`Open cart, ${cartCount} items`}
        >
          {cartCount > 0 && (
            <span style={{
              background: 'var(--gold-light)',
              color: 'var(--dark)',
              borderRadius: '50%',
              width: 20, height: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '.72rem',
              fontWeight: 800,
            }}
            >
              {cartCount}
            </span>
          )}
          Order now
        </button>
      </div>

      <div
        style={{
          display: 'none',
          alignItems: 'center',
          gap: '1rem',
          paddingLeft: '2rem',
        }}
        className="nav-mobile-cluster"
      >
        <button
          ref={burgerRef}
          type="button"
          onClick={openMobileMenu}
          className={`nav-burger nav-burger--toolbar${menuOpen ? ' nav-burger--toolbar-hidden' : ''}`}
          aria-label="Open menu"
          aria-expanded={false}
          aria-haspopup="dialog"
        >
          <span className="nav-burger-bar" />
          <span className="nav-burger-bar" />
          <span className="nav-burger-bar" />
        </button>
      </div>

      {menuOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              className={`nav-mobile-overlay${menuOverlayFadeIn && !menuOverlayClosing ? ' nav-mobile-overlay--visible' : ''}${menuOverlayClosing ? ' nav-mobile-overlay--closing' : ''}`}
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
            >
              <div className="nav-mobile-overlay-body">
                {['menu', 'specials', 'story', 'feedback', 'footer'].map((id) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    onClick={(e) => { e.preventDefault(); scrollTo(id); }}
                    className="nav-mobile-overlay-link"
                  >
                    {id === 'footer' ? 'Contact' : id.charAt(0).toUpperCase() + id.slice(1).replace('-', "'s ")}
                  </a>
                ))}
                <button
                  type="button"
                  className="nav-mobile-overlay-cta"
                  onClick={() => beginMenuClose(() => onOpenCart())}
                >
                  Order now {cartCount > 0 && `(${cartCount})`}
                </button>
              </div>
            </div>
            <button
              type="button"
              className="nav-burger nav-burger--toolbar"
              style={
                burgerAnchorRect
                  ? {
                      position: 'fixed',
                      top: burgerAnchorRect.top,
                      left: burgerAnchorRect.left,
                      width: burgerAnchorRect.width,
                      height: burgerAnchorRect.height,
                      zIndex: 2100,
                      margin: 0,
                    }
                  : {
                      position: 'fixed',
                      top: 'max(12px, env(safe-area-inset-top, 0px))',
                      right: 'max(1rem, env(safe-area-inset-right, 0px))',
                      width: 44,
                      height: 44,
                      zIndex: 2100,
                      margin: 0,
                    }
              }
              aria-label="Close menu"
              aria-expanded={overlayMenuIconExpanded}
              onClick={closeMobileMenuWithMorph}
            >
              <span className="nav-burger-bar" />
              <span className="nav-burger-bar" />
              <span className="nav-burger-bar" />
            </button>
          </>,
          document.body
        )}

      <style>{`
        .main-nav > *:not(.nav-logo-home):not(.nav-backdrop-layer) {
          position: relative;
          z-index: 1;
        }
        .nav-logo-mark--gold path {
          fill: url(#navLogoGoldGrad);
        }
        .nav-logo-spacer {
          flex-shrink: 0;
          height: 1px;
          transition: width 0.45s cubic-bezier(0.4, 0, 0.2, 1);
        }
        /* Top: 25px + logo (114×1.2×1.2); scrolled: 2rem + bar logo — keeps header height unchanged */
        .main-nav--top .nav-logo-spacer {
          width: calc(25px + 164.16px);
        }
        .main-nav--scrolled .nav-logo-spacer {
          width: calc(2rem + 58px);
        }
        .nav-logo-home {
          position: absolute;
          z-index: 2;
          transition:
            top 0.45s cubic-bezier(0.4, 0, 0.2, 1),
            left 0.45s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .main-nav--top .nav-logo-home {
          top: 25px;
          left: 25px;
        }
        .main-nav--scrolled .nav-logo-home {
          top: 0;
          left: 2rem;
        }
        /* At top: base 114px × 1.44 (20% + 20%); scrolled: original bar size */
        .main-nav--top .nav-logo-mark {
          width: 164.16px;
          height: 164.16px;
        }
        .main-nav--scrolled .nav-logo-mark {
          width: 58px;
          height: 58px;
        }
        .nav-mobile-overlay {
          position: fixed;
          inset: 0;
          width: 100%;
          min-height: 100%;
          min-height: 100dvh;
          z-index: 2000;
          display: flex;
          flex-direction: column;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          opacity: 0;
          transition: opacity ${MENU_BURGER_MORPH_MS}ms ease;
          background: linear-gradient(165deg, rgba(7, 52, 62, 0.98) 0%, rgba(8, 22, 26, 0.99) 100%);
          -webkit-backdrop-filter: blur(18px) saturate(1.1);
          backdrop-filter: blur(18px) saturate(1.1);
        }
        .nav-mobile-overlay--visible:not(.nav-mobile-overlay--closing) {
          opacity: 1;
        }
        .nav-mobile-overlay--closing {
          opacity: 0;
        }
        .nav-mobile-overlay-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 0.2rem;
          padding: 0.5rem 2rem 2rem;
          padding-bottom: max(2rem, env(safe-area-inset-bottom));
        }
        .nav-mobile-overlay-link {
          color: rgba(255, 255, 255, 0.92);
          text-decoration: none;
          font-size: 1.1rem;
          font-weight: 500;
          letter-spacing: 0.02em;
          padding: 0.65rem 0;
          width: 100%;
          max-width: 300px;
          text-align: center;
          transition: color 0.18s;
        }
        .nav-mobile-overlay-link:active {
          color: var(--gold-light);
        }
        .nav-mobile-overlay-cta {
          margin-top: 1.1rem;
          background: var(--red);
          color: white;
          border: none;
          padding: 0.85rem 1.5rem;
          border-radius: var(--radius-pill);
          font-weight: 700;
          font-size: 1rem;
          width: 100%;
          max-width: 300px;
          cursor: pointer;
          font-family: var(--font-body), system-ui, sans-serif;
        }
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-cluster { display: flex !important; }
          .main-nav--top .nav-logo-spacer {
            width: calc(25px + 139.68px);
          }
          .main-nav--scrolled .nav-logo-spacer {
            width: calc(2rem + 50px);
          }
          .main-nav--scrolled .nav-logo-home {
            top: 4px;
          }
          .main-nav--top .nav-logo-mark {
            width: 139.68px;
            height: 139.68px;
          }
          .main-nav--scrolled .nav-logo-mark {
            width: 50px;
            height: 50px;
          }
          .nav-burger--toolbar {
            display: block;
            position: relative;
            width: 44px;
            height: 44px;
            padding: 0;
            background: transparent;
            border: none;
            border-radius: 0;
            cursor: pointer;
            flex-shrink: 0;
            color: inherit;
            -webkit-tap-highlight-color: transparent;
          }
          .nav-burger--toolbar-hidden {
            visibility: hidden;
            pointer-events: none;
          }
          .nav-burger-bar {
            position: absolute;
            left: 50%;
            width: 24px;
            height: 3px;
            margin: 0;
            background: #fff;
            border-radius: 1.5px;
            transform: translateX(-50%);
            transform-origin: center;
            transition:
              transform ${MENU_BURGER_MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1),
              opacity ${MENU_BURGER_MORPH_MS}ms ease,
              top ${MENU_BURGER_MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
          }
          .nav-burger--toolbar .nav-burger-bar:nth-child(1) { top: 12px; }
          .nav-burger--toolbar .nav-burger-bar:nth-child(2) { top: 20px; }
          .nav-burger--toolbar .nav-burger-bar:nth-child(3) { top: 28px; }
          .nav-burger--toolbar[aria-expanded="true"] .nav-burger-bar:nth-child(1) {
            top: 20px;
            transform: translateX(-50%) rotate(45deg);
          }
          .nav-burger--toolbar[aria-expanded="true"] .nav-burger-bar:nth-child(2) {
            opacity: 0;
            transform: translateX(-50%) scaleX(0);
          }
          .nav-burger--toolbar[aria-expanded="true"] .nav-burger-bar:nth-child(3) {
            top: 20px;
            transform: translateX(-50%) rotate(-45deg);
          }
        }
      `}</style>
    </nav>
    </div>
  );
}
